import React, { useState, useEffect } from 'react';
import Modal from '../../BaseComponents/Modal';
import Loader from '../../BaseComponents/Loader';
import './Files.scss';

interface ViewFileModalProps {
    isOpen: boolean;
    onClose: () => void;
    file: any;
}

const ViewFileModal: React.FC<ViewFileModalProps> = ({ isOpen, onClose, file }) => {
    const [isLoading, setIsLoading] = useState(true);

    const fileName = file?.fileName || file?.file_name || '';
    const fileUrl = file?.url || file?.view_url || '';

    const isPDF = fileName.toLowerCase().endsWith('.pdf') ||
        fileUrl.toLowerCase().includes('.pdf');

    const isDoc = /\.(doc|docx)$/i.test(fileName) ||
        fileUrl.toLowerCase().includes('.doc');

    const isImage = !!file && (/\.(jpg|jpeg|png|gif|webp)$/i.test(fileName) ||
        (file.type && typeof file.type === 'string' && file.type.toLowerCase().includes('image')));

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);

            if (!isPDF && !isDoc && !isImage) {
                setIsLoading(false);
            }
        }
    }, [isOpen, fileUrl, isPDF, isDoc, isImage]);

    if (!file) return null;

    const handleLoad = () => {
        setIsLoading(false);
    };

    const renderPreview = () => {
        if (isPDF) {
            return (
                <iframe
                    src={`${fileUrl}${fileUrl.includes('#') ? '&' : '#'}toolbar=0`}
                    width="100%"
                    height="800px"
                    title="PDF Viewer"
                    onLoad={handleLoad}
                    style={{ border: 'none', display: isLoading ? 'none' : 'block' }}
                ></iframe>
            );
        }

        if (isDoc) {
            return (
                <iframe
                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
                    width="100%"
                    height="800px"
                    title="Document Viewer"
                    onLoad={handleLoad}
                    style={{ border: 'none', display: isLoading ? 'none' : 'block' }}
                ></iframe>
            );
        }

        if (isImage) {
            return (
                <div className="image-preview-container" style={{ display: isLoading ? 'none' : 'flex', justifyContent: 'center' }}>
                    <img
                        src={fileUrl}
                        alt={fileName}
                        onLoad={handleLoad}
                        style={{ maxWidth: '100%', maxHeight: '800px', objectFit: 'contain' }}
                    />
                </div>
            );
        }

        return (
            <div className="document-preview" style={{ padding: '40px', textAlign: 'center' }}>
                <p style={{ color: '#666' }}>Preview is not supported for this file type.</p>
                <p><strong>{fileName}</strong></p>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`File Name = ${fileName} | Type = ${file.type || 'Document'}`}
            width="800px"
        >
            <div className="view-file-modal-content" style={{ position: 'relative', minHeight: '200px' }}>
                {isLoading && (
                    <div className="loader-overlay" style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '400px',
                        width: '100%'
                    }}>
                        <Loader />
                    </div>
                )}
                {renderPreview()}
            </div>
        </Modal>
    );
};

export default ViewFileModal;
